import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { collectionPath } from "../context/EnvContext";
import { useFirestore } from "../firebase/FirestoreContext";
import { calculateCart } from "../functions/calculateCart";
import useWallet from "./useWallet";
import noop from "../functions/noop";

export const CheckoutProvider = {
  wallet: "Wallet",
  stripe: "Stripe",
};

export default function useCart() {
  const [refreshTime, setRefreshTime] = useState(null);
  const [refreshedAtTime, setRefreshedAtTime] = useState(null);
  const refresh = () => setRefreshTime(Date.now());
  const firestore = useFirestore();
  const { currentUser: user } = getAuth();
  const wallet = useWallet();
  const [cartItems, setCartItems] = useState([]);
  const [appliedDiscount, setAppliedDiscount] = useState(); // For applied discount details
  const [selectedShippingData, setSelectedShippingData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [shippingReady, setShippingReady] = useState(false);

  const [settings, setSettings] = useState({
    shippingThreshold: 0,
    perishablePrice: 0,
    shippingCost: 0,
  });

  const [cart, setCart] = useState();

  useEffect(() => {
    if (!user?.uid) return noop;

    const fetchCart = async () => {
      const q = query(
        collection(firestore, `${collectionPath}Carts/CartsList/DataBase`),
        where("user_id", "==", user.uid),
        where("status", "==", "ordering"),
      );

      const snapshot = await getDocs(q);
      const [doc] = snapshot.docs;

      if (!doc) {
        setIsReady(true);
        return;
      }

      setCart({
        id: doc.id,
        ...doc.data(),
      });

      if (refreshTime) {
        setRefreshedAtTime(Date.now());
      }
    };
    fetchCart();
  }, [user?.uid, firestore, refreshTime]);

  useEffect(() => {
    if (!cart?.id) return noop;

    const fetchItems = async () => {
      const itemsRef = collection(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase/${cart.id}/Items`,
      );
      const itemsSnapshot = await getDocs(itemsRef);

      const items = await Promise.all(
        itemsSnapshot.docs.map(async (doc) => {
          const itemData = doc.data();

          return {
            id: doc.id,
            cartID: cart.id,
            ...itemData,
            finalPrice: itemData.final_price.toFixed(2),
          };
        }),
      );

      // Sorting by seller_id
      const sortedItems = items.sort((a, b) =>
        a.seller_id > b.seller_id ? 1 : -1,
      );

      setCartItems(sortedItems);
      setIsReady(true);
    };

    fetchItems();
  }, [cart?.id, firestore, refreshTime]);

  useEffect(() => {
    const fetchData = async () => {
      const ref = doc(
        firestore,
        `${collectionPath}SooperstockSettings`,
        "Shipment",
      );
      const snapshot = await getDoc(ref);
      const { freeShipmentThreshold, perishablePrice, shipmentPrice } =
        snapshot.data();

      setSettings((s) => ({
        ...s,
        shippingThreshold: freeShipmentThreshold,
        perishablePrice,
        shippingCost: shipmentPrice,
      }));
      setShippingReady(true);
    };
    fetchData();
  }, [firestore]);

  useEffect(() => {
    if (!cart?.applied_discount_name) return noop;

    setAppliedDiscount({
      code: cart.applied_discount_name,
      name: cart.applied_discount_name,
      percentage: cart.applied_discount_percentage,
      discount: cart.applied_discount_percentage,
      discountAmount: cart.applied_discount_amount,
      minimumPurchaseAmount: cart.applied_discount_minimum_purchase_amount,
      maxAmount: cart.applied_discount_max_amount,
    });
  }, [cart]);

  console.log(selectedShippingData);
  const summary = calculateCart({
    cartItems: cartItems || [],
    appliedDiscount,
    wallet,
    settings,
    shippingData: selectedShippingData,
  });

  const checkoutProvider = useMemo(() => {
    if (!isReady) return null;
    if (summary.paymentTotals.wallet > 0 && summary.paymentTotals.cc === 0)
      return CheckoutProvider.wallet;
    if (summary.paymentTotals.cc > 0) return CheckoutProvider.stripe;

    return null;
  }, [wallet, isReady, summary.paymentTotals]);

  return {
    wallet,
    cart,
    cartItems,
    summary,
    setAppliedDiscount,
    setCartItems,
    appliedDiscount,
    selectedShippingData,
    setSelectedShippingData,
    refresh,
    refreshedAtTime,
    isReady,
    shippingReady,
    checkoutProvider,
  };
}
